import { isCosmeticUnlocked } from '../../../shared/cosmetic_unlocks.mjs'

export type CosmeticRarity = 'common' | 'rare' | 'epic' | 'legendary'

export type AvatarFrameId = string
export type ProfileBannerId = string
export type HudThemeId = string

export interface CosmeticContext {
  level: number
  badges: number
  achievements: string[]
  stats: Record<string, number>
  completedPokedexGens: number[]
  totalKills: number
  /** Paliers mécénat : 0–3 */
  supporterTier?: number
  /** Cosmétiques communautaires (jauge infra) débloqués pour tous */
  communityUnlockedCosmeticIds?: string[]
}

function stat(ctx: CosmeticContext, key: string): number {
  // KOs: stored on the player row (`ctx.totalKills`), not necessarily in `stats` JSON — same idea as useAchievements.
  if (key === 'totalKills') {
    const fromColumn = typeof ctx.totalKills === 'number' && Number.isFinite(ctx.totalKills) ? ctx.totalKills : 0
    const fromStats = ctx.stats?.totalKills
    const statsVal = typeof fromStats === 'number' && Number.isFinite(fromStats) ? fromStats : 0
    return Math.max(fromColumn, statsVal)
  }
  const v = ctx.stats?.[key]
  return typeof v === 'number' && Number.isFinite(v) ? v : 0
}

const adminCosmeticOverrideUnlock = (ctx: CosmeticContext, cosmeticId: string) =>
  stat(ctx, `_adminCosmeticGrant:${cosmeticId}`) >= 1

const LIFETIME_COSMETIC_IDS_KEY = '_lifetimeUnlockedCosmetics'
const MAX_LIFETIME_COSMETIC_IDS = 128

export function getLifetimeUnlockedCosmeticIdSet(ctx: CosmeticContext): Set<string> {
  const raw = (ctx.stats as Record<string, unknown> | undefined)?.[LIFETIME_COSMETIC_IDS_KEY]
  if (!Array.isArray(raw)) return new Set()
  return new Set(
    raw.filter((id): id is string => typeof id === 'string' && id.length > 0 && id.length <= 80),
  )
}

function isLifetimeUnlockedCosmetic(ctx: CosmeticContext, cosmeticId: string): boolean {
  return getLifetimeUnlockedCosmeticIdSet(ctx).has(cosmeticId)
}

/** IDs actuellement débloqués par règle (cadres, bannières, HUD) — pour persistance après ascension. */
export function collectRuleUnlockedCosmeticIds(ctx: CosmeticContext): string[] {
  const ids = [
    ...getUnlockedAvatarFrames(ctx).map((f) => f.id),
    ...getUnlockedProfileBanners(ctx).map((b) => b.id),
    ...HUD_THEMES.filter((h) => {
      try {
        return h.unlock(ctx) || adminCosmeticOverrideUnlock(ctx, h.id)
      } catch {
        return false
      }
    }).map((h) => h.id),
  ]
  return [...new Set(ids)]
}

export function mergeLifetimeUnlockedCosmeticIds(
  stats: Record<string, number> | Record<string, unknown> | null | undefined,
  ids: Iterable<string>,
): Record<string, unknown> {
  const base = { ...(stats ?? {}) } as Record<string, unknown>
  const merged = new Set([...getLifetimeUnlockedCosmeticIdSet({ stats: base as Record<string, number>, level: 0, badges: 0, achievements: [], completedPokedexGens: [], totalKills: 0 }), ...ids])
  base[LIFETIME_COSMETIC_IDS_KEY] = [...merged].slice(0, MAX_LIFETIME_COSMETIC_IDS)
  return base
}

export interface AvatarFrameDef {
  id: AvatarFrameId
  nameFr: string
  nameEn: string
  rarity: CosmeticRarity
  className: string
  unlock: (ctx: CosmeticContext) => boolean
}

export interface ProfileBannerDef {
  id: ProfileBannerId
  nameFr: string
  nameEn: string
  rarity: CosmeticRarity
  className: string
  pokemonId?: number
  pokemonOffsetX?: string
  pokemonOffsetY?: string
  unlock: (ctx: CosmeticContext) => boolean
}

export interface HudThemeDef {
  id: HudThemeId
  nameFr: string
  nameEn: string
  rarity: CosmeticRarity
  /** CSS class applied on the game layout root */
  className: string
  unlock: (ctx: CosmeticContext) => boolean
}

export const COSMETIC_RARITY_DECOR: Record<CosmeticRarity, string> = {
  common: '•',
  rare: '◆',
  epic: '✦',
  legendary: '👑',
}

export const COSMETIC_RARITY_LABELS: Record<CosmeticRarity, { fr: string; en: string }> = {
  common: { fr: 'Commun', en: 'Common' },
  rare: { fr: 'Rare', en: 'Rare' },
  epic: { fr: 'Épique', en: 'Epic' },
  legendary: { fr: 'Légendaire', en: 'Legendary' },
}

export function cosmeticRarityClass(rarity: CosmeticRarity): string {
  if (rarity === 'legendary') return 'border-amber-300/70 bg-amber-500/15 text-amber-100'
  if (rarity === 'epic') return 'border-fuchsia-300/70 bg-fuchsia-500/15 text-fuchsia-100'
  if (rarity === 'rare') return 'border-cyan-300/70 bg-cyan-500/15 text-cyan-100'
  return 'border-slate-500/60 bg-slate-700/50 text-slate-100'
}

// ── Avatar frames ──
export const AVATAR_FRAMES: AvatarFrameDef[] = [
  {
    id: 'frame_basic',
    nameFr: 'Classique',
    nameEn: 'Classic',
    rarity: 'common',
    className: 'cos-frame-basic',
    unlock: () => true,
  },
  {
    id: 'frame_kanto',
    nameFr: 'Champion Kanto',
    nameEn: 'Kanto Champion',
    rarity: 'rare',
    className: 'cos-frame-kanto',
    unlock: (ctx) => isCosmeticUnlocked('frame_kanto', ctx),
  },
  {
    id: 'frame_shiny',
    nameFr: 'Étincelle',
    nameEn: 'Sparkle',
    rarity: 'epic',
    className: 'cos-frame-shiny',
    unlock: (ctx) => isCosmeticUnlocked('frame_shiny', ctx),
  },
  {
    id: 'frame_arena',
    nameFr: 'Gladiateur',
    nameEn: 'Gladiator',
    rarity: 'epic',
    className: 'cos-frame-arena',
    unlock: (ctx) => isCosmeticUnlocked('frame_arena', ctx),
  },
  {
    id: 'frame_master',
    nameFr: 'Couronne',
    nameEn: 'Crown',
    rarity: 'legendary',
    className: 'cos-frame-master',
    unlock: (ctx) => isCosmeticUnlocked('frame_master', ctx),
  },
  /** Style « social » (inspiration générique type apps de chat, sans reprendre d’assets tiers). */
  {
    id: 'frame_social_blurple',
    nameFr: 'Anneau Néon',
    nameEn: 'Neon Ring',
    rarity: 'rare',
    className: 'cos-frame-social-blurple',
    unlock: (ctx) => isCosmeticUnlocked('frame_social_blurple', ctx),
  },
  {
    id: 'frame_nitro_aura',
    nameFr: 'Aura Premium',
    nameEn: 'Premium Aura',
    rarity: 'epic',
    className: 'cos-frame-nitro-aura',
    unlock: (ctx) => isCosmeticUnlocked('frame_nitro_aura', ctx),
  },
  {
    id: 'frame_boost_fuchsia',
    nameFr: 'Rayon Boost',
    nameEn: 'Boost Ray',
    rarity: 'epic',
    className: 'cos-frame-boost-fuchsia',
    unlock: (ctx) => isCosmeticUnlocked('frame_boost_fuchsia', ctx),
  },
  {
    id: 'frame_asc_eligible',
    nameFr: 'Éligible à l’ascension',
    nameEn: 'Ascension eligible',
    rarity: 'epic',
    className: 'cos-frame-asc-eligible',
    unlock: (ctx) => isCosmeticUnlocked('frame_asc_eligible', ctx),
  },
  {
    id: 'frame_asc_ember',
    nameFr: 'Braise Ascendante',
    nameEn: 'Ascending Ember',
    rarity: 'epic',
    className: 'cos-frame-asc-ember',
    unlock: (ctx) => isCosmeticUnlocked('frame_asc_ember', ctx),
  },
  {
    id: 'frame_asc_celestial',
    nameFr: 'Voile Céleste',
    nameEn: 'Celestial Veil',
    rarity: 'legendary',
    className: 'cos-frame-asc-celestial',
    unlock: (ctx) => isCosmeticUnlocked('frame_asc_celestial', ctx),
  },
  {
    id: 'frame_ember_core',
    nameFr: 'Noyau de Braise',
    nameEn: 'Ember Core',
    rarity: 'rare',
    className: 'cos-frame-ember-core',
    unlock: (ctx) => isCosmeticUnlocked('frame_ember_core', ctx),
  },
  {
    id: 'frame_tidal_crown',
    nameFr: 'Couronne des Marées',
    nameEn: 'Tidal Crown',
    rarity: 'rare',
    className: 'cos-frame-tidal-crown',
    unlock: (ctx) => isCosmeticUnlocked('frame_tidal_crown', ctx),
  },
  {
    id: 'frame_shadow_orbit',
    nameFr: 'Orbite des Ombres',
    nameEn: 'Shadow Orbit',
    rarity: 'epic',
    className: 'cos-frame-shadow-orbit',
    unlock: (ctx) => isCosmeticUnlocked('frame_shadow_orbit', ctx),
  },
  {
    id: 'frame_glacier_sigil',
    nameFr: 'Sceau Glaciaire',
    nameEn: 'Glacier Sigil',
    rarity: 'epic',
    className: 'cos-frame-glacier-sigil',
    unlock: (ctx) => isCosmeticUnlocked('frame_glacier_sigil', ctx),
  },
  {
    id: 'frame_stellar_halo',
    nameFr: 'Halo Stellaire',
    nameEn: 'Stellar Halo',
    rarity: 'legendary',
    className: 'cos-frame-stellar-halo',
    unlock: (ctx) => isCosmeticUnlocked('frame_stellar_halo', ctx),
  },
  {
    id: 'frame_royal_onyx',
    nameFr: 'Onyx Royal',
    nameEn: 'Royal Onyx',
    rarity: 'legendary',
    className: 'cos-frame-royal-onyx',
    unlock: (ctx) => isCosmeticUnlocked('frame_royal_onyx', ctx),
  },
  {
    id: 'frame_asc_dawn',
    nameFr: 'Aube Ascendante',
    nameEn: 'Ascending Dawn',
    rarity: 'legendary',
    className: 'cos-frame-asc-dawn',
    unlock: (ctx) => isCosmeticUnlocked('frame_asc_dawn', ctx),
  },
  {
    id: 'frame_void_paragon',
    nameFr: 'Parangon du Néant',
    nameEn: 'Void Paragon',
    rarity: 'legendary',
    className: 'cos-frame-void-paragon',
    unlock: (ctx) => isCosmeticUnlocked('frame_void_paragon', ctx),
  },
  {
    id: 'frame_asc_season_bronze',
    nameFr: 'Saison Bronze',
    nameEn: 'Bronze Season',
    rarity: 'common',
    className: 'cos-frame-asc-dawn',
    unlock: (ctx) => isCosmeticUnlocked('frame_asc_season_bronze', ctx),
  },
  {
    id: 'frame_asc_overkill',
    nameFr: 'Overkill',
    nameEn: 'Overkill',
    rarity: 'epic',
    className: 'cos-frame-asc-dawn',
    unlock: (ctx) => isCosmeticUnlocked('frame_asc_overkill', ctx),
  },
  {
    id: 'frame_asc_season_violet',
    nameFr: 'Saison Violette',
    nameEn: 'Violet Season',
    rarity: 'epic',
    className: 'cos-frame-asc-dawn',
    unlock: (ctx) => isCosmeticUnlocked('frame_asc_season_violet', ctx),
  },
  {
    id: 'frame_asc_season_apex',
    nameFr: 'Apex de Saison',
    nameEn: 'Season Apex',
    rarity: 'legendary',
    className: 'cos-frame-void-paragon',
    unlock: (ctx) => isCosmeticUnlocked('frame_asc_season_apex', ctx),
  },
  {
    id: 'frame_mythic_triad',
    nameFr: 'Triade Mythique',
    nameEn: 'Mythic Triad',
    rarity: 'legendary',
    className: 'cos-frame-admin-voidcrest',
    unlock: (ctx) => isCosmeticUnlocked('frame_mythic_triad', ctx),
  },
  {
    id: 'frame_admin_dragonforged',
    nameFr: 'Forge Draconique',
    nameEn: 'Dragonforged',
    rarity: 'legendary',
    className: 'cos-frame-admin-dragonforged',
    unlock: (ctx) => isCosmeticUnlocked('frame_admin_dragonforged', ctx),
  },
  {
    id: 'frame_admin_aurora_crown',
    nameFr: 'Couronne Aurore',
    nameEn: 'Aurora Crown',
    rarity: 'legendary',
    className: 'cos-frame-admin-aurora-crown',
    unlock: (ctx) => isCosmeticUnlocked('frame_admin_aurora_crown', ctx),
  },
  {
    id: 'frame_admin_voidcrest',
    nameFr: 'Sceau du Néant',
    nameEn: 'Voidcrest',
    rarity: 'legendary',
    className: 'cos-frame-admin-voidcrest',
    unlock: (ctx) => isCosmeticUnlocked('frame_admin_voidcrest', ctx),
  },
  {
    id: 'frame_patron_glow',
    nameFr: 'Aura Mécène',
    nameEn: 'Patron Glow',
    rarity: 'epic',
    className: 'cos-frame-patron-glow',
    unlock: (ctx) => isCosmeticUnlocked('frame_patron_glow', ctx),
  },
  {
    id: 'frame_patron_aura',
    nameFr: 'Couronne Bienfaiteur',
    nameEn: 'Benefactor Crown',
    rarity: 'legendary',
    className: 'cos-frame-patron-aura',
    unlock: (ctx) => isCosmeticUnlocked('frame_patron_aura', ctx),
  },
  {
    id: 'frame_community_spark',
    nameFr: 'Étincelle Communautaire',
    nameEn: 'Community Spark',
    rarity: 'rare',
    className: 'cos-frame-community-spark',
    unlock: (ctx) => isCosmeticUnlocked('frame_community_spark', ctx),
  },
]

export function getAvatarFrameById(id: string | null | undefined): AvatarFrameDef | undefined {
  if (!id) return undefined
  return AVATAR_FRAMES.find((f) => f.id === id)
}

export function getUnlockedAvatarFrames(ctx: CosmeticContext): AvatarFrameDef[] {
  return AVATAR_FRAMES.filter((f) => {
    try {
      return (
        isLifetimeUnlockedCosmetic(ctx, f.id) ||
        f.unlock(ctx) ||
        adminCosmeticOverrideUnlock(ctx, f.id)
      )
    } catch {
      return false
    }
  })
}

// ── Profile banners ──
export const PROFILE_BANNERS: ProfileBannerDef[] = [
  {
    id: 'banner_default',
    nameFr: 'Nuit Indigo',
    nameEn: 'Indigo Night',
    rarity: 'common',
    className: 'cos-banner-default',
    pokemonId: 94,
    pokemonOffsetX: '6px',
    pokemonOffsetY: '-4px',
    unlock: () => true,
  },
  {
    id: 'banner_pokedex',
    nameFr: 'Maître Pokédex',
    nameEn: 'Pokédex Master',
    rarity: 'legendary',
    className: 'cos-banner-pokedex',
    pokemonId: 150,
    pokemonOffsetX: '6px',
    pokemonOffsetY: '-8px',
    unlock: (ctx) => isCosmeticUnlocked('banner_pokedex', ctx),
  },
  {
    id: 'banner_arena',
    nameFr: 'Arène Néon',
    nameEn: 'Neon Arena',
    rarity: 'epic',
    className: 'cos-banner-arena',
    pokemonId: 448,
    pokemonOffsetX: '4px',
    pokemonOffsetY: '-6px',
    unlock: (ctx) => isCosmeticUnlocked('banner_arena', ctx),
  },
  {
    id: 'banner_olympus',
    nameFr: 'Olympe',
    nameEn: 'Olympus',
    rarity: 'legendary',
    className: 'cos-banner-olympus',
    pokemonId: 249,
    pokemonOffsetX: '2px',
    pokemonOffsetY: '-10px',
    unlock: (ctx) => isCosmeticUnlocked('banner_olympus', ctx),
  },
  {
    id: 'banner_asc_nebula',
    nameFr: 'Nébuleuse du Cycle',
    nameEn: 'Cycle Nebula',
    rarity: 'epic',
    className: 'cos-banner-asc-nebula',
    pokemonId: 282,
    pokemonOffsetX: '8px',
    pokemonOffsetY: '-6px',
    unlock: (ctx) => isCosmeticUnlocked('banner_asc_nebula', ctx),
  },
  {
    id: 'banner_asc_eternal',
    nameFr: 'Serment Éternel',
    nameEn: 'Eternal Oath',
    rarity: 'legendary',
    className: 'cos-banner-asc-eternal',
    pokemonId: 384,
    pokemonOffsetX: '0px',
    pokemonOffsetY: '-12px',
    unlock: (ctx) => isCosmeticUnlocked('banner_asc_eternal', ctx),
  },
  {
    id: 'banner_sunset_plaza',
    nameFr: 'Place Crépusculaire',
    nameEn: 'Sunset Plaza',
    rarity: 'rare',
    className: 'cos-banner-sunset-plaza',
    pokemonId: 197,
    pokemonOffsetX: '8px',
    pokemonOffsetY: '-6px',
    unlock: (ctx) => isCosmeticUnlocked('banner_sunset_plaza', ctx),
  },
  {
    id: 'banner_midnight_lake',
    nameFr: 'Lac de Minuit',
    nameEn: 'Midnight Lake',
    rarity: 'rare',
    className: 'cos-banner-midnight-lake',
    pokemonId: 130,
    pokemonOffsetX: '4px',
    pokemonOffsetY: '-8px',
    unlock: (ctx) => isCosmeticUnlocked('banner_midnight_lake', ctx),
  },
  {
    id: 'banner_crystal_spire',
    nameFr: 'Flèche de Cristal',
    nameEn: 'Crystal Spire',
    rarity: 'epic',
    className: 'cos-banner-crystal-spire',
    pokemonId: 373,
    pokemonOffsetX: '2px',
    pokemonOffsetY: '-10px',
    unlock: (ctx) => isCosmeticUnlocked('banner_crystal_spire', ctx),
  },
  {
    id: 'banner_stormfront',
    nameFr: 'Front d’Orage',
    nameEn: 'Stormfront',
    rarity: 'epic',
    className: 'cos-banner-stormfront',
    pokemonId: 145,
    pokemonOffsetX: '6px',
    pokemonOffsetY: '-8px',
    unlock: (ctx) => isCosmeticUnlocked('banner_stormfront', ctx),
  },
  {
    id: 'banner_golden_summit',
    nameFr: 'Sommet Doré',
    nameEn: 'Golden Summit',
    rarity: 'legendary',
    className: 'cos-banner-golden-summit',
    pokemonId: 248,
    pokemonOffsetX: '4px',
    pokemonOffsetY: '-12px',
    unlock: (ctx) => isCosmeticUnlocked('banner_golden_summit', ctx),
  },
  {
    id: 'banner_obsidian_throne',
    nameFr: 'Trône d’Obsidienne',
    nameEn: 'Obsidian Throne',
    rarity: 'legendary',
    className: 'cos-banner-obsidian-throne',
    pokemonId: 150,
    pokemonOffsetX: '2px',
    pokemonOffsetY: '-10px',
    unlock: (ctx) => isCosmeticUnlocked('banner_obsidian_throne', ctx),
  },
  {
    id: 'banner_asc_aurora',
    nameFr: 'Aurore Ascendante',
    nameEn: 'Ascending Aurora',
    rarity: 'legendary',
    className: 'cos-banner-asc-aurora',
    pokemonId: 249,
    pokemonOffsetX: '0px',
    pokemonOffsetY: '-12px',
    unlock: (ctx) => isCosmeticUnlocked('banner_asc_aurora', ctx),
  },
  {
    id: 'banner_asc_paragon',
    nameFr: 'Parangon Astral',
    nameEn: 'Astral Paragon',
    rarity: 'legendary',
    className: 'cos-banner-asc-paragon',
    pokemonId: 384,
    pokemonOffsetX: '0px',
    pokemonOffsetY: '-14px',
    unlock: (ctx) => isCosmeticUnlocked('banner_asc_paragon', ctx),
  },
  {
    id: 'banner_asc_season_starter',
    nameFr: 'Saison Commençante',
    nameEn: 'Starter Season',
    rarity: 'rare',
    className: 'cos-banner-asc-aurora',
    pokemonId: 25,
    pokemonOffsetX: '0px',
    pokemonOffsetY: '-10px',
    unlock: (ctx) => isCosmeticUnlocked('banner_asc_season_starter', ctx),
  },
  {
    id: 'banner_asc_season_celestial',
    nameFr: 'Saison Céleste',
    nameEn: 'Celestial Season',
    rarity: 'legendary',
    className: 'cos-banner-asc-aurora',
    pokemonId: 249,
    pokemonOffsetX: '0px',
    pokemonOffsetY: '-12px',
    unlock: (ctx) => isCosmeticUnlocked('banner_asc_season_celestial', ctx),
  },
  {
    id: 'banner_mythic_omega',
    nameFr: 'Serment Oméga Mythique',
    nameEn: 'Mythic Omega Oath',
    rarity: 'legendary',
    className: 'cos-banner-admin-crimson-gate',
    pokemonId: 150,
    pokemonOffsetX: '0px',
    pokemonOffsetY: '-12px',
    unlock: (ctx) => isCosmeticUnlocked('banner_mythic_omega', ctx),
  },
  {
    id: 'banner_admin_radiant_sky',
    nameFr: 'Ciel Radieux',
    nameEn: 'Radiant Sky',
    rarity: 'legendary',
    className: 'cos-banner-admin-radiant-sky',
    pokemonId: 249,
    pokemonOffsetX: '0px',
    pokemonOffsetY: '-12px',
    unlock: (ctx) => isCosmeticUnlocked('banner_admin_radiant_sky', ctx),
  },
  {
    id: 'banner_admin_crimson_gate',
    nameFr: 'Porte Cramoisie',
    nameEn: 'Crimson Gate',
    rarity: 'legendary',
    className: 'cos-banner-admin-crimson-gate',
    pokemonId: 383,
    pokemonOffsetX: '0px',
    pokemonOffsetY: '-14px',
    unlock: (ctx) => isCosmeticUnlocked('banner_admin_crimson_gate', ctx),
  },
  {
    id: 'banner_admin_astral_archive',
    nameFr: 'Archive Astrale',
    nameEn: 'Astral Archive',
    rarity: 'legendary',
    className: 'cos-banner-admin-astral-archive',
    pokemonId: 384,
    pokemonOffsetX: '0px',
    pokemonOffsetY: '-14px',
    unlock: (ctx) => isCosmeticUnlocked('banner_admin_astral_archive', ctx),
  },
  {
    id: 'banner_patron_sky',
    nameFr: 'Ciel Mécène',
    nameEn: 'Patron Sky',
    rarity: 'epic',
    className: 'cos-banner-patron-sky',
    pokemonId: 448,
    pokemonOffsetX: '4px',
    pokemonOffsetY: '-8px',
    unlock: (ctx) => isCosmeticUnlocked('banner_patron_sky', ctx),
  },
  {
    id: 'banner_patron_golden',
    nameFr: 'Sommet Doré',
    nameEn: 'Golden Summit',
    rarity: 'legendary',
    className: 'cos-banner-patron-golden',
    pokemonId: 250,
    pokemonOffsetX: '0px',
    pokemonOffsetY: '-12px',
    unlock: (ctx) => isCosmeticUnlocked('banner_patron_golden', ctx),
  },
  {
    id: 'banner_community_unity',
    nameFr: 'Unité Communautaire',
    nameEn: 'Community Unity',
    rarity: 'epic',
    className: 'cos-banner-community-unity',
    pokemonId: 151,
    pokemonOffsetX: '0px',
    pokemonOffsetY: '-10px',
    unlock: (ctx) => isCosmeticUnlocked('banner_community_unity', ctx),
  },
]

export function getProfileBannerById(id: string | null | undefined): ProfileBannerDef | undefined {
  if (!id) return undefined
  return PROFILE_BANNERS.find((b) => b.id === id)
}

export function getUnlockedProfileBanners(ctx: CosmeticContext): ProfileBannerDef[] {
  return PROFILE_BANNERS.filter((b) => {
    try {
      return (
        isLifetimeUnlockedCosmetic(ctx, b.id) ||
        b.unlock(ctx) ||
        adminCosmeticOverrideUnlock(ctx, b.id)
      )
    } catch {
      return false
    }
  })
}

// ── HUD themes ──
export const HUD_THEMES: HudThemeDef[] = [
  {
    id: 'hud_classic',
    nameFr: 'Classique',
    nameEn: 'Classic',
    rarity: 'common',
    className: 'hud-theme-classic',
    unlock: () => true,
  },
  {
    id: 'hud_neon',
    nameFr: 'Néon',
    nameEn: 'Neon',
    rarity: 'rare',
    className: 'hud-theme-neon',
    unlock: (ctx) => isCosmeticUnlocked('hud_neon', ctx),
  },
  {
    id: 'hud_gold',
    nameFr: 'Or Royal',
    nameEn: 'Royal Gold',
    rarity: 'epic',
    className: 'hud-theme-gold',
    unlock: (ctx) => isCosmeticUnlocked('hud_gold', ctx),
  },
  {
    id: 'hud_void',
    nameFr: 'Abysses',
    nameEn: 'Void',
    rarity: 'legendary',
    className: 'hud-theme-void',
    unlock: (ctx) => isCosmeticUnlocked('hud_void', ctx),
  },
  {
    id: 'hud_kanto_retro',
    nameFr: 'Kanto Retro',
    nameEn: 'Kanto Retro',
    rarity: 'rare',
    className: 'hud-theme-kanto-retro',
    unlock: (ctx) => isCosmeticUnlocked('hud_kanto_retro', ctx),
  },
  {
    id: 'hud_johto_pocket',
    nameFr: 'Johto Pocket',
    nameEn: 'Johto Pocket',
    rarity: 'epic',
    className: 'hud-theme-johto-pocket',
    unlock: (ctx) => isCosmeticUnlocked('hud_johto_pocket', ctx),
  },
  {
    id: 'hud_hoenn_advance',
    nameFr: 'Hoenn Advance',
    nameEn: 'Hoenn Advance',
    rarity: 'epic',
    className: 'hud-theme-hoenn-advance',
    unlock: (ctx) => isCosmeticUnlocked('hud_hoenn_advance', ctx),
  },
  {
    id: 'hud_champion_pixels',
    nameFr: 'Champion Pixels',
    nameEn: 'Champion Pixels',
    rarity: 'legendary',
    className: 'hud-theme-champion-pixels',
    unlock: (ctx) => isCosmeticUnlocked('hud_champion_pixels', ctx),
  },
  {
    id: 'hud_pixel_town',
    nameFr: 'Ville Pixel',
    nameEn: 'Pixel Town',
    rarity: 'rare',
    className: 'hud-theme-pixel-town',
    unlock: (ctx) => isCosmeticUnlocked('hud_pixel_town', ctx),
  },
  {
    id: 'hud_pixel_route',
    nameFr: 'Route Pixel',
    nameEn: 'Pixel Route',
    rarity: 'epic',
    className: 'hud-theme-pixel-route',
    unlock: (ctx) => isCosmeticUnlocked('hud_pixel_route', ctx),
  },
  {
    id: 'hud_pixel_cave',
    nameFr: 'Grotte Pixel',
    nameEn: 'Pixel Cave',
    rarity: 'epic',
    className: 'hud-theme-pixel-cave',
    unlock: (ctx) => isCosmeticUnlocked('hud_pixel_cave', ctx),
  },
  {
    id: 'hud_pixel_league',
    nameFr: 'Ligue Pixel',
    nameEn: 'Pixel League',
    rarity: 'legendary',
    className: 'hud-theme-pixel-league',
    unlock: (ctx) => isCosmeticUnlocked('hud_pixel_league', ctx),
  },
  {
    id: 'hud_biome_forest',
    nameFr: 'Biome Foret',
    nameEn: 'Forest Biome',
    rarity: 'rare',
    className: 'hud-theme-biome-forest',
    unlock: (ctx) => isCosmeticUnlocked('hud_biome_forest', ctx),
  },
  {
    id: 'hud_biome_ocean',
    nameFr: 'Biome Ocean',
    nameEn: 'Ocean Biome',
    rarity: 'epic',
    className: 'hud-theme-biome-ocean',
    unlock: (ctx) => isCosmeticUnlocked('hud_biome_ocean', ctx),
  },
  {
    id: 'hud_biome_volcano',
    nameFr: 'Biome Volcan',
    nameEn: 'Volcano Biome',
    rarity: 'epic',
    className: 'hud-theme-biome-volcano',
    unlock: (ctx) => isCosmeticUnlocked('hud_biome_volcano', ctx),
  },
  {
    id: 'hud_biome_snow',
    nameFr: 'Biome Neige',
    nameEn: 'Snow Biome',
    rarity: 'legendary',
    className: 'hud-theme-biome-snow',
    unlock: (ctx) => isCosmeticUnlocked('hud_biome_snow', ctx),
  },
  {
    id: 'hud_asc_crimson',
    nameFr: 'Ascension Cramoisie',
    nameEn: 'Crimson Ascension',
    rarity: 'epic',
    className: 'hud-theme-asc-crimson',
    unlock: (ctx) => isCosmeticUnlocked('hud_asc_crimson', ctx),
  },
  {
    id: 'hud_asc_astral',
    nameFr: 'Ascension Astrale',
    nameEn: 'Astral Ascension',
    rarity: 'legendary',
    className: 'hud-theme-asc-astral',
    unlock: (ctx) => isCosmeticUnlocked('hud_asc_astral', ctx),
  },
  {
    id: 'hud_asc_paragon',
    nameFr: 'Parangon Oméga',
    nameEn: 'Omega Paragon',
    rarity: 'legendary',
    className: 'hud-theme-asc-paragon',
    unlock: (ctx) => isCosmeticUnlocked('hud_asc_paragon', ctx),
  },
  {
    id: 'hud_asc_season_nova',
    nameFr: 'Nova de Saison',
    nameEn: 'Season Nova',
    rarity: 'legendary',
    className: 'hud-theme-asc-astral',
    unlock: (ctx) => isCosmeticUnlocked('hud_asc_season_nova', ctx),
  },
  {
    id: 'hud_mythic_command',
    nameFr: 'Commandement Mythique',
    nameEn: 'Mythic Command',
    rarity: 'legendary',
    className: 'hud-theme-admin-void-protocol',
    unlock: (ctx) => isCosmeticUnlocked('hud_mythic_command', ctx),
  },
  {
    id: 'hud_admin_celestial_command',
    nameFr: 'Commandement Céleste',
    nameEn: 'Celestial Command',
    rarity: 'legendary',
    className: 'hud-theme-admin-celestial-command',
    unlock: (ctx) => isCosmeticUnlocked('hud_admin_celestial_command', ctx),
  },
  {
    id: 'hud_admin_crimson_royal',
    nameFr: 'Royal Cramoisie',
    nameEn: 'Crimson Royal',
    rarity: 'legendary',
    className: 'hud-theme-admin-crimson-royal',
    unlock: (ctx) => isCosmeticUnlocked('hud_admin_crimson_royal', ctx),
  },
  {
    id: 'hud_admin_void_protocol',
    nameFr: 'Protocole du Néant',
    nameEn: 'Void Protocol',
    rarity: 'legendary',
    className: 'hud-theme-admin-void-protocol',
    unlock: (ctx) => isCosmeticUnlocked('hud_admin_void_protocol', ctx),
  },
  {
    id: 'hud_patron_celestial',
    nameFr: 'Ciel de Mécène',
    nameEn: 'Patron Celestial',
    rarity: 'epic',
    className: 'hud-theme-patron-celestial',
    unlock: (ctx) => isCosmeticUnlocked('hud_patron_celestial', ctx),
  },
  {
    id: 'hud_patron_shimmer',
    nameFr: 'Lueur Bienfaiteur',
    nameEn: 'Benefactor Shimmer',
    rarity: 'legendary',
    className: 'hud-theme-patron-shimmer',
    unlock: (ctx) => isCosmeticUnlocked('hud_patron_shimmer', ctx),
  },
  {
    id: 'hud_community_glow',
    nameFr: 'Lueur Communautaire',
    nameEn: 'Community Glow',
    rarity: 'rare',
    className: 'hud-theme-community-glow',
    unlock: (ctx) => isCosmeticUnlocked('hud_community_glow', ctx),
  },
  {
    id: 'hud_community_radiance',
    nameFr: 'Éclat Communautaire',
    nameEn: 'Community Radiance',
    rarity: 'epic',
    className: 'hud-theme-community-radiance',
    unlock: (ctx) => isCosmeticUnlocked('hud_community_radiance', ctx),
  },
]

export function getHudThemeById(id: string | null | undefined): HudThemeDef | undefined {
  if (!id) return undefined
  return HUD_THEMES.find((h) => h.id === id)
}

export function getUnlockedHudThemes(ctx: CosmeticContext): HudThemeDef[] {
  const ownedHud = (ctx.stats as Record<string, unknown> | undefined)?._ownedHudThemes
  const ownedSet = Array.isArray(ownedHud)
    ? new Set(ownedHud.filter((id): id is string => typeof id === 'string' && id.length > 0))
    : new Set<string>()
  return HUD_THEMES.filter((h) => {
    try {
      return (
        ownedSet.has(h.id) ||
        isLifetimeUnlockedCosmetic(ctx, h.id) ||
        h.unlock(ctx) ||
        adminCosmeticOverrideUnlock(ctx, h.id)
      )
    } catch {
      return false
    }
  })
}

export function isCosmeticUnlockedByRuleOrAdminOverride(
  ctx: CosmeticContext,
  cosmetic: { id: string; unlock: (ctx: CosmeticContext) => boolean }
): boolean {
  try {
    return (
      isLifetimeUnlockedCosmetic(ctx, cosmetic.id) ||
      cosmetic.unlock(ctx) ||
      adminCosmeticOverrideUnlock(ctx, cosmetic.id)
    )
  } catch {
    return false
  }
}
